<template>
  <q-dialog v-model="aberto" persistent>
    <q-card style="min-width: 560px; max-width: 640px; border-radius: 4px">
      <q-card-section class="q-pb-sm">
        <div class="row items-center justify-between no-wrap">
          <div class="text-h6 text-weight-bold" style="color: #21324a">
            Vincular Riscos ao CA
          </div>
          <q-btn icon="close" flat round dense @click="fechar" />
        </div>
        <div class="text-body2 text-grey-7 q-mt-xs">
          Selecione múltiplos riscos para vincular a um único CA.
        </div>
      </q-card-section>

      <q-separator />

      <q-card-section>
        <div class="text-body2 text-weight-bold text-brand-5 q-mb-xs">
          Selecione o CA: *
        </div>
        <q-select
          v-model="caSelecionado"
          dense
          outlined
          use-input
          hide-selected
          fill-input
          input-debounce="300"
          :options="casFiltrados"
          placeholder="Selecione um CA"
          @filter="filtrarCAs"
          :loading="carregandoCAs"
          hide-bottom-space
        >
          <template v-slot:no-option>
            <q-item>
              <q-item-section>Nenhum CA encontrado</q-item-section>
            </q-item>
          </template>
        </q-select>
      </q-card-section>

      <q-card-section class="q-pt-none">
        <div class="text-body2 text-weight-bold text-brand-5 q-mb-xs">
          Selecione os Riscos:
        </div>
        <q-input
          v-model="buscaRisco"
          dense
          outlined
          placeholder="Buscar riscos..."
          class="q-mb-sm"
          @update:model-value="filtrarRiscos"
        >
          <template v-slot:prepend>
            <q-icon name="search" />
          </template>
        </q-input>

        <div
          style="
            max-height: 260px;
            overflow-y: auto;
            border: 1px solid #e0e0e0;
            border-radius: 4px;
          "
          class="q-pa-xs"
        >
          <q-inner-loading :showing="carregandoRiscos" />
          <q-list dense v-if="riscosFiltrados.length > 0">
            <q-item
              v-for="risco in riscosFiltrados"
              :key="risco.id"
              tag="label"
              clickable
            >
              <q-item-section avatar>
                <q-checkbox v-model="riscosSelecionados" :val="risco" dense />
              </q-item-section>
              <q-item-section>
                <q-item-label>
                  {{ risco.code }} - {{ risco.name }}
                </q-item-label>
                <q-item-label caption>
                  Criado em: {{ formatarData(risco.createdAt) }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
          <div
            v-else-if="!carregandoRiscos"
            class="text-center text-grey-6 q-pa-md"
          >
            Nenhum risco encontrado
          </div>
        </div>

        <div class="text-caption text-grey-6 q-mt-sm">
          {{ riscosSelecionados.length }} risco(s) selecionado(s)
        </div>
      </q-card-section>

      <q-card-actions align="right" class="q-px-md q-pb-md">
        <q-btn
          flat
          no-caps
          label="Cancelar"
          style="color: #21324a; border: 1px solid #21324a; border-radius: 4px"
          @click="fechar"
        />
        <q-btn
          no-caps
          label="Criar Vínculos"
          :loading="salvando"
          :disable="riscosSelecionados.length === 0 || !caSelecionado"
          style="background-color: #1a73e8; color: white; border-radius: 4px"
          @click="salvar"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { ref, watch } from "vue";
import { useStore } from "vuex";
import { useQuasar } from "quasar";

export default {
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    vendorId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ["update:modelValue", "vinculos-criados"],
  setup(props, { emit }) {
    const store = useStore();
    const quasar = useQuasar();
    const aberto = ref(false);
    const salvando = ref(false);
    const carregandoCAs = ref(false);
    const carregandoRiscos = ref(false);

    const caSelecionado = ref(null);
    const riscosSelecionados = ref([]);
    const buscaRisco = ref("");

    const casFiltrados = ref([]);
    const todosOsRiscos = ref([]);
    const riscosFiltrados = ref([]);

    const formatarData = (data) => {
      if (!data) return "-";
      try {
        return new Date(data).toLocaleDateString("pt-BR");
      } catch {
        return data;
      }
    };

    const carregarCAs = async () => {};

    const carregarRiscos = async () => {
      if (!props.vendorId) return;
      carregandoRiscos.value = true;
      try {
        const resp = await store.dispatch("gestaoDeRiscos/listarRiscos", {
          vendorId: props.vendorId,
          page: 1,
          perPage: 999,
        });
        todosOsRiscos.value =
          resp.data?.result?.data ?? resp.data?.resultado ?? [];
        riscosFiltrados.value = [...todosOsRiscos.value];
      } catch {
        todosOsRiscos.value = [];
        riscosFiltrados.value = [];
      } finally {
        carregandoRiscos.value = false;
      }
    };

    const filtrarCAs = (val, update) => {
      setTimeout(async () => {
        try {
          carregandoCAs.value = true;
          const resp = await store.dispatch("gestaoDeRiscos/listarCAs", {
            code: val,
          });
          casFiltrados.value = (resp.data?.results?.data ?? []).map((ca) => ({
            value: ca.id,
            label: `${ca.code} - ${ca.status}`,
          }));
          update();
        } catch {
          casFiltrados.value = [];
          update();
        } finally {
          carregandoCAs.value = false;
        }
      }, 300);
    };

    const filtrarRiscos = (val) => {
      if (!val) {
        riscosFiltrados.value = [...todosOsRiscos.value];
        return;
      }
      const needle = val.toLowerCase();
      riscosFiltrados.value = todosOsRiscos.value.filter(
        (r) =>
          r.code?.toLowerCase().includes(needle) ||
          r.name?.toLowerCase().includes(needle)
      );
    };

    watch(
      () => props.modelValue,
      (val) => {
        aberto.value = val;
        if (val) {
          caSelecionado.value = null;
          riscosSelecionados.value = [];
          buscaRisco.value = "";
          carregarCAs();
          carregarRiscos();
        }
      }
    );

    watch(aberto, (val) => {
      if (!val) emit("update:modelValue", false);
    });

    const fechar = () => {
      aberto.value = false;
    };

    const salvar = async () => {
      if (!caSelecionado.value || riscosSelecionados.value.length === 0) return;

      salvando.value = true;
      let erros = 0;

      for (const risco of riscosSelecionados.value) {
        try {
          await store.dispatch("gestaoDeRiscos/vincularRiscoCA", {
            id: risco.id,
            riskControlType: "ca",
            riskControlExternalId: caSelecionado.value.value,
          });
        } catch {
          erros++;
        }
      }

      salvando.value = false;

      if (erros === 0) {
        quasar.notify({
          message: "Vínculos criados com sucesso!",
          color: "positive",
          position: "bottom",
          icon: "check_circle",
        });
        emit("vinculos-criados");
        fechar();
      } else {
        quasar.notify({
          message: `${erros} vínculo(s) não foram criados. Tente novamente.`,
          color: "warning",
          position: "bottom",
        });
      }
    };

    return {
      aberto,
      salvando,
      carregandoCAs,
      carregandoRiscos,
      caSelecionado,
      riscosSelecionados,
      buscaRisco,
      casFiltrados,
      riscosFiltrados,
      filtrarCAs,
      filtrarRiscos,
      formatarData,
      fechar,
      salvar,
    };
  },
};
</script>
